import { TheBoatDBConfigType, useConfig } from '@marine/configs';
import Image from 'next/image';
import { FC } from 'react';
import { SanityImage } from 'sanity-image';
import BoatImagePlaceholder from '../../../../packages/sailor-assets/svg/no-image-placeholder.svg';
import { BoatCardImageProps } from './BoatCardImage.props';

const BoatCardImage: FC<BoatCardImageProps> = ({ image, height, width }) => {
  const { projectId, dataset } = useConfig<TheBoatDBConfigType>();
  const imageAssetRef = typeof image === 'object' && image !== null ? image.asset?._ref : undefined;
  const imageUrl =
    typeof image === 'string' ? image : typeof image === 'object' && image !== null ? image.url : undefined;
  const imageAlt =
    typeof image === 'object' && image !== null ? image.alt || image.alternativeText || 'Boat Image' : 'Boat Image';

  if (imageAssetRef) {
    return (
      <SanityImage
        id={imageAssetRef}
        projectId={projectId}
        dataset={dataset}
        width={width}
        height={height}
        alt={imageAlt}
      />
    );
  }

  if (imageUrl) {
    return <Image src={imageUrl} width={width} height={height} alt={imageAlt} />;
  }

  // Rendered as a component, not handed to `next/image` as a `src`. The turbopack rule in
  // next.config.js runs `*.svg` through @svgr/webpack, so this import is a React component — passing
  // it as `src` produced `<img src="">`, which makes the browser re-request the whole document, and
  // logged "Image is missing required src property" for every boat with no image. The three other
  // placeholder call sites in this app (BoatGallery, CompareBoatCard, DiscoverBoats) already render
  // it this way.
  //
  // Note `custom.d.ts` types the default `*.svg` export as `string`, which is why the wrong usage
  // type-checked. That declaration does not match the @svgr/webpack output.
  return (
    <div style={{ width: '100%', height, overflow: 'hidden' }}>
      <BoatImagePlaceholder
        role="img"
        aria-label="No image available for this boat"
        style={{ width: '100%', height: '100%', display: 'block' }}
        preserveAspectRatio="xMidYMid slice"
      />
    </div>
  );
};

export { BoatCardImage };
